import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Modal from '../components/Modal';
import PrepwiseLogo from '../components/PrepwiseLogo';
import './DevLogin.css';

const DevLogin: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  
  // Modal state
  const [modal, setModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'info' | 'warning';
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
  });

  const handleAdminLogin = async () => {
    try {
      // First, ensure admin is registered in database
      const registerResponse = await fetch(`${API_URL}/auth/register-admin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: 'admin@test.com',
          name: 'Admin User',
          id: 'dev-admin',
        }),
      });

      if (!registerResponse.ok) {
        const errorData = await registerResponse.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to register admin');
      }

      // Then login
      await login({
        id: 'dev-admin',
        email: 'admin@test.com',
        name: 'Admin User',
      });
      
      // Navigate to admin dashboard
      navigate('/admin/dashboard');
    } catch (error: any) {
      console.error('Dev admin login error:', error);
      setModal({
        isOpen: true,
        title: 'Login Failed',
        message: error.message || 'Failed to login as admin. Please try again.',
        type: 'error',
      });
    }
  };

  const handleEmployeeLogin = () => {
    login({
      id: 'dev-employee',
      email: 'employee@test.com',
      name: 'Employee User',
    });
    navigate('/dashboard');
  };

  return (
    <div className="dev-login-container">
      <Modal
        isOpen={modal.isOpen}
        onClose={() => setModal({ ...modal, isOpen: false })}
        title={modal.title}
        message={modal.message}
        type={modal.type}
      />
      
      <div className="dev-login-card">
        <div className="dev-login-header">
          <div className="dev-login-logo-container">
            <PrepwiseLogo size="large" />
            <h1>Prepwise</h1>
          </div>
          <p className="dev-login-subtitle">Development Login</p>
          <p className="dev-login-description">
            Quick access for development and testing purposes only.
          </p>
        </div>
        
        <div className="dev-login-options">
          <div className="dev-login-warning">
            <p>⚠️ Development Mode</p>
            <p className="dev-login-warning-small">
              This page is for development and testing only. Do not use in production.
            </p>
          </div>

          <button
            className="dev-login-button admin-button"
            onClick={handleAdminLogin}
          >
            <svg className="dev-login-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
              <path d="M2 17l10 5 10-5M2 12l10 5 10-5"></path>
            </svg>
            Login as Admin (Dev)
          </button>

          <button
            className="dev-login-button employee-button"
            onClick={handleEmployeeLogin}
          >
            <svg className="dev-login-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
            Login as Employee (Dev)
          </button>
        </div>

        <div className="dev-login-footer">
          <p>
            <a href="/login" className="dev-login-link">
              ← Back to Login
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default DevLogin;

